import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Fruits.css";

interface FruitItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Fruits: React.FC = () => {
  const [data, setData] = useState<FruitItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/fruits`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching fruits:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: FruitItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: FruitItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Fruits",
    de: "Früchte",
    ta: "பழங்கள்",
    si: "පළතුරු",
    hi: "फल",
    ar: "فواكه",
    tr: "Meyveler",
    ru: "Фрукты",
    zh: "水果",
    fr: "Fruits",
    es: "Frutas",
  };

  const subtitleMap = {
    en: "Fruits in German",
    de: "Früchte auf Deutsch",
    ta: "ஜெர்மன் மொழியில் பழங்கள்",
    si: "ජර්මන් භාෂාවෙන් පළතුරු",
    hi: "जर्मन में फल",
    ar: "الفواكه بالألمانية",
    tr: "Almanca'da Meyveler",
    ru: "Фрукты на немецком",
    zh: "德语水果",
    fr: "Fruits en allemand",
    es: "Frutas en alemán",
  };

  return (
    <div className="fruits-page-container">
      <div className="fruits-page-header">
        <div>
          <h1 className="fruits-page-title">{titleMap["de"]}</h1>
          <p className="fruits-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="fruits-page-loading">Loading...</div>
      ) : (
        <div className="fruits-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;

            return (
              <div
                key={item.id}
                className={`fruits-page-card ${isPlaying ? "fruits-page-playing" : ""}`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="fruits-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="fruits-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="fruits-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="fruits-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="fruits-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })} 
        </div>
      )}
    </div>
  );
};

export default Fruits;
